/**
 * InitializeData.tsx
 *
 * This file handles the initialization of application data on component mount.
 * It manages the loading of user account data, main application state, and note/folder data.
 *
 * Process:
 * 1. Wait for Supabase auth listener to restore session (via useSupabaseContext)
 * 2. Load cached user account data from localStorage
 * 3. Load mainStore and dataStore from IndexedDB using user ID
 * 4. Handle three cases:
 *    - No valid user cached: load default stores
 *    - Same user as before: skip initialization
 *    - New/different cached user: load user data and sync with cloud
 * 5. Sync local data with cloud data after initialization
 *
 * Auth Flow:
 * - SupabaseProvider sets up onAuthStateChange listener
 * - Supabase automatically saves/restores session to localStorage
 * - Auth listener syncs session changes to useAccountManagementStore
 * - This hook reacts to store changes and loads appropriate data
 *
 * Events handled by auth listener:
 * - INITIAL_SESSION: Page load, Supabase restored session from localStorage
 * - SIGNED_IN: User logged in via credentials or OAuth
 * - TOKEN_REFRESHED: Supabase auto-refreshed expired tokens
 * - SIGNED_OUT: User logged out
 */

import { useEffect } from "react";
import useAccountManagementStore from "./stores/accountManagementStore";
import { getUserAccount } from "./utility/cache/storeUserAccount";
import { loadDataStore } from "./utility/cache/manageDataStore";
import useDataStore, { DataStoreType } from "./stores/dataStore";
import { loadMainStore } from "./utility/cache/manageMainStore";
import useMainStore, { MainStoreType } from "./stores/mainStore";
import cloudSyncHandler from "./utility/cloudSyncHandler";
import { supabase } from "./context/useSupabaseContext";

let lastLoginUser = undefined;

function useInitializeData() {
    const userID = useAccountManagementStore((state) => state.userData?.id);
    const setUserData = useAccountManagementStore((state) => state.setUserData);

    useEffect(() => {
        const initialize = async () => {
            // Loading from local storage is faster than loading from supabase.
            const userAccountData = getUserAccount();
            const isUserLoggedIn = userAccountData?.id;

            if (!isUserLoggedIn) {
                loadDataStore_();
                loadMainStore_();
                lastLoginUser = undefined;
                return;
            }

            if (lastLoginUser === userAccountData.id) return;

            useMainStore.getState().actions.unload();
            lastLoginUser = userAccountData.id;

            loadDataStore_(userAccountData.id);
            loadMainStore_(userAccountData.id);


            // Once supabase link is established, sync
            await supabase.auth.getUser().then(({data})=>{
                if(data.user.id){
                    // Sync changes between local and cloud data
                    cloudSyncHandler(userAccountData);
                }
            }).catch(()=>{
                // User failed logging in, load default stores
                loadDataStore_();
                loadMainStore_();
                lastLoginUser = undefined;                
            })

        };

        initialize();
        // const timeoutId = setTimeout(initialize, 100);

        // return () => clearTimeout(timeoutId);
    }, [userID, setUserData]);

    return null;
}

export default useInitializeData;

/**
 * Loads the dataStore from IndexedDB for a given userID.
 * If userID is undefined, it defaults to "default".
 * Then, it parses the JSON string and sets the dataStore with the parsed data.
 * @param userID The ID of the user to load the dataStore for.
 */
async function loadDataStore_(userID?: string) {
    try {
        const dataStoreString = await loadDataStore(userID);

        const data: DataStoreType["data"] = JSON.parse(dataStoreString);
        useDataStore.getState().set.data(() => data);
    } catch (error) {
        console.error("Failed to load data store:", error);
    }
}

/**
 * Loads the mainStore from IndexedDB for a given userID.
 * If userID is undefined, it defaults to "default".
 * Then, it parses the JSON string and sets the mainStore with the parsed data.
 * @param userID The ID of the user to load the mainStore for.
 */
async function loadMainStore_(userID?: string) {
    try {
        const mainStoreString = await loadMainStore(userID);

        const parsedData: MainStoreType["data"] = JSON.parse(mainStoreString);

        useMainStore.getState().set.data(() => ({
            ...parsedData,
            loaded: true,
            userID: userID,
        }));
    } catch (error) {
        console.error("Failed to load main store:", error);
    }
}
